import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  addDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  increment, 
  Timestamp, 
  arrayUnion, 
  arrayRemove, 
  runTransaction, 
  collectionGroup, 
  or,
  and, 
  writeBatch,
  startAfter 
} from 'firebase/firestore';
import { db, auth, callFirebaseFunction } from './firebase';
import * as aiService from './aiService';
import { toast } from './components/Toast'; 

// --- CONSTANTS ---
export const REGION_GLOBAL = 'INTL';

// THE "DECK" OF CONQUEST REGIONS
const ALL_CONQUEST_REGIONS = [
    // US States
    'TX', 'CA', 'NY', 'FL', 'WA', 'IL', 'PA', 'OH', 'GA', 'NC', 'MI',
    // Major Powers (ISO Codes)
    'CN', 'RU', 'GB', 'FR', 'DE', 'JP', 'IN', 'BR', 'CA', 'AU', 'IT', 'KR', 'ES', 'MX', 'ID', 'TR', 'SA', 'ZA'
];

// --- HELPER: REPUTATION SYSTEM ---
const REP_VALUES = {
    UPVOTE: 5,
    DOWNVOTE: -2,
    AI_BONUS: 10,
    INSIGHT_AWARD: 50,
    COMMENDATION: 25 
};

// --- HELPER: XP REWARDS FOR INTERACTION ---
const XP_REWARDS = {
    SAME_SIDE: 10,
    NEUTRAL: 20,
    OPPOSITE_SIDE: 50,
    COMMENDATION: 100 
};

// --- HELPER: SAFE TIMESTAMP CONVERSION ---
const getTimestampMillis = (ts) => {
    if (!ts) return null;
    // Handle Firestore Timestamp object (has toMillis)
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    // Handle serialized object (has seconds)
    if (ts.seconds) return ts.seconds * 1000;
    // Handle JS Date object
    if (ts instanceof Date) return ts.getTime();
    // Handle raw number
    if (typeof ts === 'number') return ts;
    return null;
};

// Helper to safely update user rep AND House rep (BUFFERED)
export const updateUserRep = async (uid, change) => {
    if (!uid || change === 0) return;
    const userRef = doc(db, 'users', uid);
    try {
        await runTransaction(db, async (transaction) => {
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) return;
            
            const userData = userSnap.data();
            
            // Update User Rep (Direct - Users don't have high contention)
            transaction.set(userRef, { stats: { repTotal: increment(change) } }, { merge: true });
            
            // Update House Rep (Buffered - Fixes Hot Document Death)
            if (userData.houseId) {
                const eventRef = doc(collection(db, 'houseEvents'));
                transaction.set(eventRef, {
                    houseId: userData.houseId,
                    rep: change,
                    createdAt: serverTimestamp()
                });
            }
        });
    } catch (error) {
        console.error("Error updating rep:", error);
    }
};

// --- TOPIC FUNCTIONS ---

// Decay "hotness" so a debate stays featured WHILE it's being argued, then cools over time —
// so a lively debate is never buried by the constant stream of fresh news (Reddit/HN-style).
export const debateHotness = (t) => {
  const activity = (t.postCount || 0) + (t.participantCount || 0) * 0.5 + (t.commentCount || 0) * 0.3;
  if (activity <= 0) return 0;
  let last = Date.now();
  if (t.lastActivityAt?.toDate) last = t.lastActivityAt.toDate().getTime();
  else if (t.lastActivityAt?.seconds) last = t.lastActivityAt.seconds * 1000;
  else if (t.isoDate) last = new Date(t.isoDate).getTime();
  const hours = Math.max(0, (Date.now() - last) / 3600000);
  return activity / Math.pow(hours + 2, 1.5);
};

// Hot Debates strip (news-first): surface the debates worth having right now — the most-argued
// AND the most polarizing fresh stories — so it's compelling even before anyone has posted.
// Two single-field queries (postCount, polarization) → auto-indexed, no composite index needed.
export const getHotDebates = async (count = 24) => {
  try {
    const [hotSnap, polSnap] = await Promise.all([
      getDocs(query(collection(db, 'topics'), orderBy('postCount', 'desc'), limit(count))),
      getDocs(query(collection(db, 'topics'), orderBy('polarization', 'desc'), limit(count))),
    ]);
    const byId = new Map();
    hotSnap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
    polSnap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
    return [...byId.values()]
      .filter(t => t.status === 'active' && !t.isEvergreen && (t.debateQuestion || (t.postCount || 0) > 0))
      .sort((a, b) => {
        // Active debates (people arguing) first; then the spiciest fresh takes.
        const ha = debateHotness(a), hb = debateHotness(b);
        if (hb !== ha) return hb - ha;
        return (b.polarization || 0) - (a.polarization || 0);
      });
  } catch (e) {
    console.error('Error fetching hot debates:', e);
    return [];
  }
};

// Timeless (evergreen) debates — their own tab, so they don't clutter the news river.
// Single equality filter → no composite index needed.
export const getEvergreenTopics = async (count = 40) => {
  try {
    const q = query(collection(db, 'topics'), where('isEvergreen', '==', true), limit(count));
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(t => t.status === 'active')
      .sort((a, b) => (b.postCount || 0) - (a.postCount || 0));
  } catch (e) {
    console.error('Error fetching evergreen topics:', e);
    return [];
  }
};

// The Debates tab: active debates (people arguing) + AI-selected debate-worthy news (controversial
// stories that passed the AI's controversy test at ingestion), even with zero posts yet — so the
// tab keeps filling with fresh things worth debating as RSS comes in. Two single-field queries → no
// composite index. Ranked: hottest active debates first, then fresh controversial news.
export const getDebatesFeed = async (count = 30) => {
  try {
    const [hotSnap, worthySnap] = await Promise.all([
      getDocs(query(collection(db, 'topics'), orderBy('postCount', 'desc'), limit(count))),
      getDocs(query(collection(db, 'topics'), where('isDebateWorthy', '==', true), limit(count))),
    ]);
    const byId = new Map();
    hotSnap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
    worthySnap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
    return [...byId.values()]
      .filter(t => t.status === 'active')
      .filter(t => (t.postCount || 0) > 0 || t.isDebateWorthy)
      .sort((a, b) => {
        const ha = debateHotness(a), hb = debateHotness(b);
        if (hb !== ha) return hb - ha;                 // hottest active debates first
        return new Date(b.isoDate || 0) - new Date(a.isoDate || 0); // then freshest controversial news
      });
  } catch (e) {
    console.error('Error fetching debates feed:', e);
    return [];
  }
};

export const getTopics = async ({ category, regionCode, communityId = 'all', count = 20, status = 'active', lastDoc = null }) => {
  try {
    let q = collection(db, 'topics');
    
    // Split filters (where/or) from modifiers (orderBy/limit/startAfter)
    // to fix "InvalidQuery: composite filters" error
    let filters = [];
    let modifiers = [];

    // 1. Status Filter (exclude archived topics by default)
    if (status === 'any') {
        // FETCH EVERYTHING including archived
    } else if (status === 'all') {
        filters.push(where('status', 'in', ['active', 'completed']));
    } else if (status === 'archived') {
        // Allow explicitly fetching archived topics
        filters.push(where('status', '==', 'archived'));
    } else {
        // Default: only active topics (exclude archived)
        filters.push(where('status', '==', status));
    }

    // 2. Modifiers (Order/Limit)
    modifiers.push(orderBy('postCount', 'desc'));
    modifiers.push(orderBy('createdAt', 'desc'));
    
    if (lastDoc) {
        modifiers.push(startAfter(lastDoc));
    }
    
    modifiers.push(limit(count));

    // 3. Community Filter
    if (communityId === 'global') {
        filters.push(where('communityId', '==', null));
    } else if (communityId && communityId !== 'all') {
        filters.push(where('communityId', '==', communityId));
    }

    // 4. Category Filter
    if (category) {
        filters.push(where('category', '==', category));
    }

    // 5. Region Filter
    if (regionCode) {
        if (regionCode.includes(':')) {
            const [country, state] = regionCode.split(':');
            filters.push(where('region.countryCode', '==', country));
            filters.push(where('region.stateCode', '==', state));
        }
        else if (['US', 'CA', 'UK', 'AU'].includes(regionCode)) {
            filters.push(where('region.countryCode', '==', regionCode));
        }
        else {
            // Case C: "TX" (State Code Only - Standard) - Uses OR
            filters.push(or(
                where('region.countryCode', '==', regionCode),
                where('region.stateCode', '==', regionCode)
            ));
        }
    }

    // Apply Filters & Modifiers
    // If multiple filters exist (and one might be 'or'), wrap in 'and()'
    if (filters.length > 1) {
        q = query(q, and(...filters), ...modifiers);
    } else if (filters.length === 1) {
        q = query(q, filters[0], ...modifiers);
    } else {
        q = query(q, ...modifiers);
    }

    let spotlightTopics = [];
    if (!lastDoc && (status === 'active' || status === 'any') && (communityId === 'all' || communityId === 'global') && !category && !regionCode) {
        try {
            const qSpotlight = query(
                collection(db, 'topics'),
                where('isSpotlight', '==', true), 
                orderBy('createdAt', 'desc'),
                limit(3)
            );
            const snapSpotlight = await getDocs(qSpotlight);
            spotlightTopics = snapSpotlight.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(t => !t.isCustom);
        } catch (e) {
            console.warn("Spotlight fetch failed (likely missing index), skipping...", e);
        }
    }

    const snapshot = await getDocs(q);
    
    // Filter post-fetch
    const standardTopics = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(t => !t.isCustom);

    // --- VELOCITY RANKING ---
    // Re-rank the fetched page by recent heat (engagement decayed by time since last
    // activity) so lively debates rise and stale ones sink — not just cumulative postCount.
    const hotness = (t) => {
        const la = t.lastActivityAt?.toMillis ? t.lastActivityAt.toMillis()
            : (t.lastActivityAt?.seconds ? t.lastActivityAt.seconds * 1000 : 0);
        const created = t.createdAt?.toMillis ? t.createdAt.toMillis()
            : (t.createdAt?.seconds ? t.createdAt.seconds * 1000 : 0);
        const lastMs = la || created || 0;
        const hoursSince = lastMs ? (Date.now() - lastMs) / 3600000 : 999;
        const recency = 1 / (1 + hoursSince / 24); // ~halves per idle day
        const engagement = (t.postCount || 0) + (t.commentCount || 0) * 0.7 + (t.insightCount || 0) * 1.5;
        const boost = t.isTopicOfDay ? 1000 : 1; // Topic of the Day rides at the top of the feed.
        return engagement * recency * boost;
    };
    standardTopics.sort((a, b) => hotness(b) - hotness(a));

    const allTopics = lastDoc ? standardTopics : [...spotlightTopics, ...standardTopics];
    const uniqueTopics = Array.from(new Set(allTopics.map(a => a.id)))
        .map(id => allTopics.find(a => a.id === id));

    return { 
        topics: uniqueTopics, 
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.size === count 
    };
  } catch (error) {
    console.error("Error fetching topics:", error);
    toast.error("Failed to load feed");
    return { topics: [], lastVisible: null, hasMore: false };
  }
};

export const getPersonalizedFeed = async (uid, count = 20, regionCode = null, lastDoc = null) => {
    // If paginating, fallback to standard feed for simplicity and stability
    if (lastDoc || !uid) return getTopics({ count, regionCode, lastDoc }); 

    try {
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};
        
        let snapshot = null;
        let personalizedTopics = [];

        // Helper to construct query with safe filtering
        const buildSafeQuery = (categories) => {
            let filters = [];
            let modifiers = [];

            // Base Filters
            filters.push(where('category', 'in', categories));
            filters.push(where('status', '==', 'active'));

            // Region Filters
            if (regionCode) {
                if (regionCode.includes(':')) {
                    const [country, state] = regionCode.split(':');
                    filters.push(where('region.countryCode', '==', country));
                    filters.push(where('region.stateCode', '==', state));
                } else if (['US', 'CA', 'UK', 'AU'].includes(regionCode)) {
                    filters.push(where('region.countryCode', '==', regionCode));
                } else {
                    filters.push(or(
                        where('region.countryCode', '==', regionCode),
                        where('region.stateCode', '==', regionCode)
                    ));
                }
            }

            // Modifiers
            modifiers.push(orderBy('postCount', 'desc'));
            modifiers.push(orderBy('createdAt', 'desc'));
            modifiers.push(limit(count));

            let q = collection(db, 'topics');
            
            if (filters.length > 1) {
                q = query(q, and(...filters), ...modifiers);
            } else {
                q = query(q, ...filters, ...modifiers);
            }
            return q;
        };

        if (userData.interests && userData.interests.length > 0) {
             const safeInterests = userData.interests.slice(0, 10);
             const q = buildSafeQuery(safeInterests);

            snapshot = await getDocs(q);
            if (snapshot.size >= 5) {
                personalizedTopics = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(t => !t.isCustom);
                
                return {
                    topics: personalizedTopics,
                    lastVisible: snapshot.docs[snapshot.docs.length - 1],
                    hasMore: snapshot.size === count
                };
            }
        }

        const { topicMap } = await getUserAnalyticsData(uid);
        const catCounts = {};
        Object.values(topicMap).forEach(cat => { 
            if (cat) catCounts[cat] = (catCounts[cat] || 0) + 1;
        });
        
        const topCats = Object.keys(catCounts)
            .sort((a,b) => catCounts[b] - catCounts[a])
            .slice(0, 5);

        if (topCats.length === 0) {
            return getTopics({ count, regionCode }); 
        } else {
            const q = buildSafeQuery(topCats);
            snapshot = await getDocs(q);
            
            if (snapshot.size < 5) {
                 const genericResult = await getTopics({ count: count - snapshot.size, regionCode }); 
                 const personalizedDocs = snapshot.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(t => !t.isCustom); 
                 
                 const ids = new Set(personalizedDocs.map(t => t.id));
                 personalizedTopics = [...personalizedDocs, ...genericResult.topics.filter(t => !ids.has(t.id))];
                 
                 return {
                     topics: personalizedTopics,
                     lastVisible: genericResult.lastVisible, 
                     hasMore: genericResult.hasMore
                 };
            } else {
                personalizedTopics = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(t => !t.isCustom);
                
                return {
                    topics: personalizedTopics,
                    lastVisible: snapshot.docs[snapshot.docs.length - 1],
                    hasMore: snapshot.size === count
                };
            }
        }
    } catch (error) {
        console.warn("Personalized feed failed, reverting to standard:", error);
        return getTopics({ count, regionCode }); 
    }
};

export const getFollowingFeed = async (uid) => {
  if (!uid) return { topics: [], lastVisible: null, hasMore: false };
  try {
    const followingRef = collection(db, 'users', uid, 'following');
    const followingSnap = await getDocs(followingRef);
    if (followingSnap.empty) return { topics: [], lastVisible: null, hasMore: false };
    
    const followedUids = followingSnap.docs.map(doc => doc.id);
    let posts = [];
    const chunks = [];
    const chunkSize = 10;
    for (let i = 0; i < followedUids.length; i += chunkSize) {
        chunks.push(followedUids.slice(i, i + chunkSize));
    }

    // Note: Pagination for following feed is tricky without complex indexes.
    // For now, we fetch latest 20 posts from followed users.
    for (const chunk of chunks) {
        const q = query(collection(db, 'posts'), where('uid', 'in', chunk), orderBy('createdAt', 'desc'), limit(20));
        const snap = await getDocs(q);
        posts = [...posts, ...snap.docs.map(d => d.data())];
    }

    const topicActivity = {};
    posts.forEach(p => {
        if (!p.topicId) return;
        if (!topicActivity[p.topicId]) topicActivity[p.topicId] = new Set();
        topicActivity[p.topicId].add(p.uid);
    });

    const topicIds = Object.keys(topicActivity);
    const enrichedTopics = await Promise.all(topicIds.map(async (tid) => {
        const tDoc = await getDoc(doc(db, 'topics', tid));
        if (!tDoc.exists()) return null;
        const activeUids = Array.from(topicActivity[tid]).slice(0, 3);
        const activeUsers = await Promise.all(activeUids.map(async (uid) => {
            const p = await getUserProfile(uid); 
            return { uid, handle: p?.handle || 'Unknown', photoURL: p?.photoURL };
        }));
        return { id: tDoc.id, ...tDoc.data(), activeFollowedUsers: activeUsers };
    }));
    
    return { 
        topics: enrichedTopics.filter(t => t !== null && !t.isCustom),
        lastVisible: null,
        hasMore: false
    };
  } catch (e) {
    console.error("Error fetching following feed:", e);
    return { topics: [], lastVisible: null, hasMore: false };
  }
};

export const getTopic = async (topicId) => {
  try {
    const docRef = doc(db, 'topics', topicId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting topic:", error);
    return null;
  }
};

export const createTopicFromArticle = async (article) => {
    if (!auth.currentUser) throw new Error("Must be logged in to create a topic.");
    if (!article || !article.link || !article.debatableTitle || !article.title) {
        throw new Error("Invalid article data provided.");
    }

    const topicsRef = collection(db, 'topics');
    
    const q = query(topicsRef, where('sourceLink', '==', article.link), limit(1));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
        return snapshot.docs[0].id;
    }

    const newTopicData = {
        title: article.debatableTitle,
        summary: article.title,
        sourceLink: article.link,
        region: { countryCode: article.countryCode || null },
        category: "Global News",
        status: 'active',
        communityId: null, 
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        postCount: 0,
        commentCount: 0,
        insightCount: 0,
        flagCount: 0,
        sides: [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }]
    };

    const docRef = await addDoc(topicsRef, newTopicData);
    return docRef.id;
};

// --- NEW: CREATE GLOBAL TOPIC ---
export const createGlobalTopic = async (topicData) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const newTopic = {
        ...topicData,
        region: { countryCode: REGION_GLOBAL, stateCode: 'Global' }, // Explicit Global Region
        category: topicData.category || "Global Affairs",
        status: 'active',
        communityId: null,
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        postCount: 0,
        commentCount: 0,
        insightCount: 0,
        flagCount: 0,
        sides: [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }],
        isSpotlight: true // Global topics usually spotlight
    };

    const docRef = await addDoc(collection(db, 'topics'), newTopic);
    return docRef.id;
};

// --- NEW: GENERATE & CREATE SYNTHETIC TOPIC ---
export const generateTrainingTopic = async (category, difficulty) => {
    try {
        const result = await callFirebaseFunction('generateTrainingTopic', { category, difficulty });
        return result;
    } catch (error) {
        console.error("Error generating topic:", error);
        throw error;
    }
};

export const createTemporaryTopic = async (topicData) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    // Status 'training' hides it from the main feed (which filters for 'active')
    const newTopic = {
        title: topicData.title,
        summary: topicData.summary,
        category: topicData.category || "General",
        sides: topicData.sides || [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }],
        region: { countryCode: 'Global' },
        status: 'training', 
        communityId: null,
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        postCount: 0,
        commentCount: 0, 
        insightCount: 0, 
        flagCount: 0,
        isSynthetic: true // Mark as AI generated
    };

    const docRef = await addDoc(collection(db, 'topics'), newTopic);
    return docRef.id;
};

export const createCommunityTopic = async (communityId, topicData) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;
    
    const communityRef = doc(db, 'communities', communityId);
    const communitySnap = await getDoc(communityRef);
    if (!communitySnap.exists) throw new Error("Community not found");
    const community = communitySnap.data();

    const memberSnap = await getDoc(doc(db, 'communities', communityId, 'members', uid));
    if (!memberSnap.exists) throw new Error("You must be a member to post topics here.");
    const member = memberSnap.data();
    const isMod = member.role === 'owner' || member.role === 'moderator';

    const status = (community.requireTopicApproval && !isMod) ? 'pending' : 'active';

    const newTopic = {
        ...topicData,
        communityId,
        createdBy: uid,
        createdAt: serverTimestamp(),
        status: status,
        postCount: 0,
        commentCount: 0, 
        insightCount: 0, 
        flagCount: 0,
        sides: [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }]
    };

    const docRef = await addDoc(collection(db, 'topics'), newTopic);
    return { id: docRef.id, status };
};

export const flagTopic = async (topicId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await updateDoc(doc(db, 'topics', topicId), {
        flagCount: increment(1)
    });
};

export const deleteTopic = async (topicId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'topics', topicId));
};

// --- COMMUNITY FUNCTIONS ---

export const getCommunities = async () => {
    try {
        const q = query(collection(db, 'communities'), orderBy('memberCount', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching communities:", error);
        toast.error("Failed to load groups");
        return [];
    }
};

export const getCommunity = async (communityId) => {
    if (!communityId) return null; 
    try {
        const docRef = doc(db, 'communities', communityId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
             return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error("Error getting community:", error);
        return null;
    }
};

// --- SECURE CREATION FUNCTIONS ---
export const createCommunity = async ({ name, description, manifesto, minReputation = 0, allowedCategories = [], requireTopicApproval = false }) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    try {
        const result = await callFirebaseFunction('createCommunitySecurely', {
            name, description, manifesto, minReputation, allowedCategories, requireTopicApproval
        });
        return result.id;
    } catch (error) {
        console.error("Error creating community:", error);
        throw error;
    }
};

export const updateCommunity = async (communityId, data) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await updateDoc(doc(db, 'communities', communityId), data);
};

export const deleteCommunity = async (communityId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'communities', communityId));
};

export const joinCommunity = async (communityId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;

    const banSnap = await getDoc(doc(db, 'communities', communityId, 'banned', uid));
    if (banSnap.exists()) {
        throw new Error("You are banned from this community.");
    }

    const community = await getCommunity(communityId);
    if (community.minReputation > 0) {
        const userStats = await getUserStats(uid);
        if ((userStats?.repTotal || 0) < community.minReputation) {
             throw new Error(`Requirement not met: Need ${community.minReputation} Reputation.`);
        }
    }

    await setDoc(doc(db, 'communities', communityId, 'members', uid), {
        uid,
        joinedAt: serverTimestamp(),
        role: 'member',
        flagCount: 0
    });

    await updateDoc(doc(db, 'communities', communityId), {
        memberCount: increment(1)
    });
    
    return true;
};

export const leaveCommunity = async (communityId) => {
     if (!auth.currentUser) throw new Error("Must be logged in");
     const uid = auth.currentUser.uid;

     await deleteDoc(doc(db, 'communities', communityId, 'members', uid));
     await updateDoc(doc(db, 'communities', communityId), {
         memberCount: increment(-1)
     });
};

export const getCommunityMembership = async (communityId, uid) => {
    if (!uid) return null;
    try {
        const snap = await getDoc(doc(db, 'communities', communityId, 'members', uid));
        return snap.exists() ? snap.data() : null;
    } catch (e) { return null; }
};

export const getCommunityMembers = async (communityId) => {
    try {
        const q = query(collection(db, 'communities', communityId, 'members'), orderBy('joinedAt', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching members:", error);
        return [];
    }
};

export const modKickMember = async (communityId, memberUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'communities', communityId, 'members', memberUid));
    await updateDoc(doc(db, 'communities', communityId), {
         memberCount: increment(-1)
    });
};

export const modBanMember = async (communityId, memberUid, reason = "Violating community rules") => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    await setDoc(doc(db, 'communities', communityId, 'banned', memberUid), {
        uid: memberUid,
        bannedAt: serverTimestamp(),
        bannedBy: auth.currentUser.uid,
        reason
    });

    try {
        await deleteDoc(doc(db, 'communities', communityId, 'members', memberUid));
        await updateDoc(doc(db, 'communities', communityId), {
             memberCount: increment(-1)
        });
    } catch (e) {
        // Ignore if already left
    }
};

export const flagCommunityMember = async (communityId, memberUid) => {
    const memberRef = doc(db, 'communities', communityId, 'members', memberUid);
    await updateDoc(memberRef, {
        flagCount: increment(1)
    });
};

// --- HOUSE (CLAN) FUNCTIONS ---

export const getHouses = async () => {
    try {
        const q = query(collection(db, 'houses'), orderBy('memberCount', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching houses:", error);
        return [];
    }
};

export const getHouse = async (houseId) => {
    if (!houseId) return null; 
    try {
        const docRef = doc(db, 'houses', houseId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
             return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error("Error getting house:", error);
        return null;
    }
};

export const getMyHouseRole = async (houseId, uid) => {
    if (!houseId || !uid) return null;
    try {
        const memberRef = doc(db, 'houses', houseId, 'members', uid);
        const memberSnap = await getDoc(memberRef);
        return memberSnap.exists() ? memberSnap.data().role : null;
    } catch (e) {
        console.error("Error getting house role:", e);
        return null;
    }
};

export const getHouseMembers = async (houseId) => {
    try {
        const q = query(collection(db, 'houses', houseId, 'members'), orderBy('joinedAt', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching house members:", error);
        return [];
    }
};

// --- SECURE HOUSE CREATION ---
export const createHouse = async ({ name, description, minReputation = 0, privacy = 'public' }) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    try {
        const result = await callFirebaseFunction('createHouseSecurely', {
            name, description, minReputation, privacy
        });
        return result.id;
    } catch (error) {
        console.error("Error creating house:", error);
        throw error;
    }
};

export const updateHouse = async (houseId, data) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const role = await getMyHouseRole(houseId, auth.currentUser.uid);
    if (role !== 'leader' && role !== 'moderator') throw new Error("Permission denied.");
    await updateDoc(doc(db, 'houses', houseId), data);
};

export const deleteHouse = async (houseId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'houses', houseId));
};

export const joinHouse = async (houseId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;

    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.exists() ? userSnap.data() : {};

    if (userData.houseId) {
        throw new Error("You are already in a House. Leave it first.");
    }

    const houseRef = doc(db, 'houses', houseId);
    const houseSnap = await getDoc(houseRef);
    if (!houseSnap.exists()) {
        throw new Error("This house does not exist.");
    }
    const houseData = houseSnap.data();
    
    if (houseData.minReputation && houseData.minReputation > 0) {
        const userRep = userData.stats?.repTotal || 0;
        if (userRep < houseData.minReputation) {
            throw new Error(`Requirement not met: Need ${houseData.minReputation} Reputation.`);
        }
    }

    // Check if banned
    const banSnap = await getDoc(doc(db, 'houses', houseId, 'banned', uid));
    if (banSnap.exists()) throw new Error("You are banned from this House.");

    // Check Privacy & SNAPSHOT STATS
    if (houseData.privacy === 'private') {
        const requestRef = doc(db, 'houses', houseId, 'joinRequests', uid);
        const requestSnap = await getDoc(requestRef);
        if (requestSnap.exists()) throw new Error("You have already requested to join this house.");

        await setDoc(requestRef, {
            uid,
            createdAt: serverTimestamp(),
            userInfo: {
                handle: userData.handle || 'Unknown',
                photoURL: userData.photoURL || null,
                repTotal: userData.stats?.repTotal || 0,
                xp: userData.stats?.xp || 0,
                winStreak: userData.stats?.winStreak || 0
            }
        });
        return 'requested'; 
    }

    const userRep = userData.stats?.repTotal || 0;
    const userXP = userData.stats?.xp || 0;

    await setDoc(doc(db, 'houses', houseId, 'members', uid), {
        uid,
        joinedAt: serverTimestamp(),
        role: 'member'
    });

    await updateDoc(doc(db, 'houses', houseId), {
        memberCount: increment(1),
        aggregateRep: increment(userRep),
        aggregateXP: increment(userXP)
    });
    
    await updateDoc(userRef, { houseId: houseId });
    return 'joined';
};

export const getHouseJoinRequests = async (houseId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    try {
        const q = query(collection(db, 'houses', houseId, 'joinRequests'), orderBy('createdAt', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
        console.error("Error fetching requests", e);
        return [];
    }
};

export const respondToJoinRequest = async (houseId, requestUid, action) => {
     if (!auth.currentUser) throw new Error("Must be logged in");
     try {
         await callFirebaseFunction('respondToJoinRequestSecurely', { houseId, requestUid, action });
     } catch (e) {
         console.error("Error responding to request:", e);
         throw e;
     }
};

export const leaveHouse = async (houseId) => {
     if (!auth.currentUser) throw new Error("Must be logged in");
     const uid = auth.currentUser.uid;
     
     const userRef = doc(db, 'users', uid);
     const userSnap = await getDoc(userRef);
     const userRep = userSnap.data()?.stats?.repTotal || 0;
     const userXP = userSnap.data()?.stats?.xp || 0;

     await deleteDoc(doc(db, 'houses', houseId, 'members', uid));
     
     await updateDoc(doc(db, 'houses', houseId), {
         memberCount: increment(-1),
         aggregateRep: increment(-userRep),
         aggregateXP: increment(-userXP)
     });
     await updateDoc(doc(db, 'users', uid), { houseId: null });
};

export const kickHouseMember = async (houseId, memberUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const memberRef = doc(db, 'houses', houseId, 'members', memberUid);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) return;

    const userRef = doc(db, 'users', memberUid);
    const userSnap = await getDoc(userRef);
    const userRep = userSnap.data()?.stats?.repTotal || 0;
    const userXP = userSnap.data()?.stats?.xp || 0;

    await deleteDoc(memberRef);
    
    await updateDoc(doc(db, 'houses', houseId), {
        memberCount: increment(-1),
        aggregateRep: increment(-userRep),
        aggregateXP: increment(-userXP)
    });
    await updateDoc(userRef, { houseId: null });
};

export const banHouseMember = async (houseId, memberUid, reason) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await setDoc(doc(db, 'houses', houseId, 'banned', memberUid), {
        uid: memberUid,
        bannedAt: serverTimestamp(),
        bannedBy: auth.currentUser.uid,
        reason
    });
    await kickHouseMember(houseId, memberUid);
};

export const setHouseMemberRole = async (houseId, memberUid, newRole) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    if (!['leader', 'moderator', 'member'].includes(newRole)) throw new Error("Invalid role");
    const memberRef = doc(db, 'houses', houseId, 'members', memberUid);
    await updateDoc(memberRef, { role: newRole });
};

export const flagHouseMember = async (houseId, memberUid) => {
    const memberRef = doc(db, 'houses', houseId, 'members', memberUid);
    await updateDoc(memberRef, { flagCount: increment(1) });
};
//------------------------------------
// --- DEBATE FUNCTIONS ---

// Lazily frame a news item into a debate (neutral question + opening args) on first open.
// Safe to call for any topic; the backend no-ops fast once a topic is already framed.
export const ensureDebateReady = async (topicId) => {
  try {
    return await callFirebaseFunction('ensureDebateReady', { topicId });
  } catch (error) {
    console.error("Error framing debate:", error);
    return { ok: false };
  }
};

export const getDebateForTopic = async (topicId) => {
  try {
    let topic = await getTopic(topicId);

    // News items are ingested cheaply with no debate framing. The first time someone opens the
    // debate, generate the neutral question + opening arguments so it reads as a real debate and
    // isn't empty. Only the first opener pays this latency; afterwards it's cached on the topic.
    if (topic?.needsFraming) {
      await ensureDebateReady(topicId);
      topic = await getTopic(topicId); // re-read: openDebateId + debateQuestion are now set
    }

    // 1. Prefer the deterministic open debate stamped on the topic by the seed trigger.
    if (topic?.openDebateId) {
      const dref = doc(db, 'debates', topic.openDebateId);
      const dsnap = await getDoc(dref);
      if (dsnap.exists()) return { id: dsnap.id, ...dsnap.data() };
    }

    // 2. Legacy topics: reuse any existing open debate.
    const qOpen = query(
        collection(db, 'debates'),
        where('topicId', '==', topicId),
        where('type', '==', 'open'),
        limit(1)
    );
    const snapshot = await getDocs(qOpen);
    if (!snapshot.empty) {
      const debateDoc = snapshot.docs[0];
      return { id: debateDoc.id, ...debateDoc.data() };
    }

    // 3. Create the single open debate with a DETERMINISTIC id, so a race with the seed
    //    trigger targets the same doc instead of forking into two debates.
    if (!topic) throw new Error("Topic not found, cannot create debate.");
    const durationHours = topic.durationHours || 72;
    const endTimestamp = Timestamp.fromDate(new Date(Date.now() + (durationHours * 60 * 60 * 1000)));
    const debateId = 'open_' + topicId;

    const newDebate = {
      topicId,
      communityId: topic.communityId || null,
      type: 'open',
      status: 'active',
      createdAt: serverTimestamp(),
      endsAt: endTimestamp,
      participantCount: 0,
      sides: topic.sides || [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }]
    };

    await setDoc(doc(db, 'debates', debateId), newDebate, { merge: true });
    await updateDoc(doc(db, 'topics', topicId), {
        endsAt: endTimestamp,
        status: 'active',
        openDebateId: debateId
    });

    return { id: debateId, ...newDebate };

  } catch (error) {
    console.error("Error getting/creating debate:", error);
    throw error;
  }
};

export const createAiDebate = async (topicId, difficulty, userSide, rounds = 3) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    try {
        const result = await callFirebaseFunction('createAiDebateSecurely', {
            topicId, difficulty, userSide, rounds
        });
        return result.id;
    } catch (error) {
        console.error("Error creating AI debate:", error);
        throw error;
    }
};

export const joinDebateStance = async (debateId, uid, side = null) => {
  if (!uid) return null;

  try {
    const stanceRef = doc(db, 'debates', debateId, 'stances', uid);
    const stanceSnap = await getDoc(stanceRef);

    if (stanceSnap.exists()) {
      return stanceSnap.data();
    }

    if (side) {
      const newStance = { uid, side, joinedAt: serverTimestamp() };
      await setDoc(stanceRef, newStance);
      return newStance;
    }

    return null;
  } catch (error) {
    console.error("Error joining debate:", error);
    return null;
  }
};

export const deleteDebate = async (debateId) => {
    if (!auth.currentUser) throw new Error("Must be logged in.");
    await deleteDoc(doc(db, 'debates', debateId));
};

// --- MULTIPLAYER ARENA FEATURES (NEW) ---

export const sendTeamChat = async (debateId, side, message) => {
    if (!auth.currentUser) throw new Error("Login required.");
    if (!message.trim()) return;
    
    await addDoc(collection(db, 'debates', debateId, 'teamChat'), {
        uid: auth.currentUser.uid,
        side, 
        message: message.trim(),
        createdAt: serverTimestamp()
    });
};

export const getTeamChat = async (debateId, side) => {
    const q = query(
        collection(db, 'debates', debateId, 'teamChat'),
        where('side', '==', side),
        orderBy('createdAt', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({id: d.id, ...d.data()}));
};

export const setTypingStatus = async (debateId, side, isTyping) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const statusRef = doc(db, 'debates', debateId, 'status', 'typing');
    
    if (isTyping) {
        await setDoc(statusRef, {
            [side]: { [uid]: Date.now() }
        }, { merge: true });
    } else {
        await updateDoc(statusRef, {
            [`${side}.${uid}`]: 0 
        });
    }
};

export const passTheTorch = async (debateId) => {
    if (!auth.currentUser) throw new Error("Login required.");
    try {
        await callFirebaseFunction('passTheTorch', { debateId });
    } catch (e) {
        console.error("Pass Torch failed:", e);
        throw e;
    }
};

export const submitCommendation = async (debateId, targetUid, type) => {
    if (!auth.currentUser) throw new Error("Login required.");
    try {
        await callFirebaseFunction('submitCommendation', { debateId, targetUid, type });
        return true;
    } catch (e) {
        console.error("Commendation failed:", e);
        throw e;
    }
};

// --- SPECTATOR FUNCTIONS ---

export const savePrediction = async (debateId, side) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;
    await setDoc(doc(db, 'debates', debateId, 'predictions', uid), {
        uid,
        side,
        predictedAt: serverTimestamp()
    });
};

export const getMyPrediction = async (debateId) => {
     if (!auth.currentUser) return null;
     try {
         const snap = await getDoc(doc(db, 'debates', debateId, 'predictions', auth.currentUser.uid));
         return snap.exists() ? snap.data().side : null;
     } catch (e) { return null; }
};

// --- COMMENTER FUNCTIONS ---

export const createDebateComment = async (debateId, body, discussedPostId = null) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    if (!body.trim()) throw new Error("Comment cannot be empty");
    const uid = auth.currentUser.uid;
    
    let topicId = null;
    try {
        const debateSnap = await getDoc(doc(db, 'debates', debateId));
        if (debateSnap.exists()) {
            const debateData = debateSnap.data();
            topicId = debateData.topicId;
            if (topicId) {
                const topicRef = doc(db, 'topics', topicId);
                await updateDoc(topicRef, { commentCount: increment(1), lastActivityAt: serverTimestamp() });
            }
        }
    } catch (e) { }

    if (discussedPostId) {
        try {
            const postRef = doc(db, 'posts', discussedPostId);
            await updateDoc(postRef, { discussCount: increment(1) });
        } catch (e) { }
    }

    let mentionedUids = [];
    const mentionRegex = /@([a-zA-Z0-9_]+)/g;
    const matches = [...body.matchAll(mentionRegex)];

    if (matches.length > 0) {
        const handles = [...new Set(matches.map(m => m[1]))];
        const chunkedHandles = [];
        for (let i = 0; i < handles.length; i += 10) {
             chunkedHandles.push(handles.slice(i, i + 10));
        }
        for (const chunk of chunkedHandles) {
             const q = query(collection(db, 'users'), where('handle', 'in', chunk));
             const snap = await getDocs(q);
             snap.forEach(d => mentionedUids.push(d.id));
        }
        mentionedUids = mentionedUids.filter(id => id !== uid);
    }

    await addDoc(collection(db, 'debates', debateId, 'comments'), {
        uid,
        body: body.trim(),
        createdAt: serverTimestamp(),
        debateId: debateId,
        topicId: topicId,
        mentionedUids: mentionedUids.length > 0 ? mentionedUids : null,
        discussedPostId: discussedPostId || null
    });
};

export const getDebateComments = async (debateId) => {
    try {
        const q = query(
            collection(db, 'debates', debateId, 'comments'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching comments:", error);
        return [];
    }
};

// --- POST FUNCTIONS (PAGINATED) ---

export const getPostsForDebate = async (debateId, lastDoc = null) => {
  try {
    let q = query(
      collection(db, 'posts'),
      where('debateId', '==', debateId),
      orderBy('createdAt', 'asc'),
      limit(20)
    );

    if (lastDoc) {
        q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return {
        posts,
        lastVisible: snapshot.docs[snapshot.docs.length - 1]
    };
  } catch (error) {
    console.error("Error fetching posts:", error);
    return { posts: [], lastVisible: null };
  }
};

export const getPostsByUser = async (uid, count = 10) => {
  try {
    const q = query(
      collection(db, 'posts'),
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(count)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching user posts:", error);
    return [];
  }
};

export const getRepliesToUser = async (uid, count = 10) => {
    try {
        const q = query(
            collection(db, 'posts'),
            where('parentUid', '==', uid),
            orderBy('createdAt', 'desc'),
            limit(count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching replies:", error);
        return [];
    }
};

export const getMentionsOfUser = async (uid, count = 10) => {
    try {
        const postsQ = query(
            collection(db, 'posts'),
            where('mentionedUids', 'array-contains', uid),
            orderBy('createdAt', 'desc'),
            limit(count)
        );
        const commentsQ = query(
            collectionGroup(db, 'comments'),
            where('mentionedUids', 'array-contains', uid),
            orderBy('createdAt', 'desc'),
            limit(count)
        );

        const [postsSnap, commentsSnap] = await Promise.all([getDocs(postsQ), getDocs(commentsQ)]);

        const posts = postsSnap.docs.map(d => ({ ...d.data(), id: d.id, type: 'post_mention' }));
        const comments = commentsSnap.docs.map(d => ({ ...d.data(), id: d.id, type: 'comment_mention' }));

        const combined = [...posts, ...comments].sort((a, b) => {
            const timeA = a.createdAt?.seconds || 0;
            const timeB = b.createdAt?.seconds || 0;
            return timeB - timeA;
        });

        return combined.slice(0, count);
    } catch (error) {
        console.error("Error fetching mentions:", error);
        return [];
    }
};

export const getPantheonPosts = async (limitCount = 20) => {
  try {
    const q = query(
        collection(db, 'posts'),
        orderBy('upvotes', 'desc'),
        limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(post => post.upvotes >= 2); 
  } catch (error) {
    console.error("Error fetching Pantheon posts:", error);
    return [];
  }
};

export const createPost = async ({ debateId, topicId, side, type, body, sourceUrl = null, parentPostId = null, turnNumber = null, phaseName = null, debateType = 'open' }) => {
  if (!auth.currentUser) throw new Error("Must be logged in");
  const uid = auth.currentUser.uid;

  try {
    let parentUid = null;
    if (parentPostId) {
        const parentSnap = await getDoc(doc(db, 'posts', parentPostId));
        if (parentSnap.exists()) {
            parentUid = parentSnap.data().uid;
        }
    }

    let mentionedUids = [];
    const mentionRegex = /@([a-zA-Z0-9_]+)/g;
    const matches = [...body.matchAll(mentionRegex)];

    if (matches.length > 0) {
        const handles = [...new Set(matches.map(m => m[1]))];
        const chunkedHandles = [];
        for (let i = 0; i < handles.length; i += 10) {
             chunkedHandles.push(handles.slice(i, i + 10));
        }
        for (const chunk of chunkedHandles) {
             const q = query(collection(db, 'users'), where('handle', 'in', chunk));
             const snap = await getDocs(q);
             snap.forEach(d => mentionedUids.push(d.id));
        }
        mentionedUids = mentionedUids.filter(id => id !== uid);
    }

    // --- NEW: AUTO-VOTE BATCH (ATOMIC) ---
    const batch = writeBatch(db);
    const postRef = doc(collection(db, 'posts'));
    
    // 1. Create Post (Start with 1 upvote)
    const postData = {
      debateId, topicId, uid, side, type, body, sourceUrl, parentPostId, turnNumber, phaseName,
      createdAt: serverTimestamp(), 
      upvotes: 1, // Start with 1 upvote
      downvotes: 0, 
      aiAnalysis: null, flagCount: 0, insightCount: 0,
      parentUid, 
      mentionedUids: mentionedUids.length > 0 ? mentionedUids : null 
    };
    batch.set(postRef, postData);

    // 2. Create Vote Doc for Author (ATOMICALLY)
    const voteRef = doc(db, 'posts', postRef.id, 'votes', uid);
    batch.set(voteRef, { 
        type: 'up', 
        votedAt: serverTimestamp() 
    });

    // NOTE: topic.postCount is maintained server-side by the analyzePostBackground
    // trigger (single source of truth — counts all posts incl. bots, decrements on
    // delete). Do not increment it here or it double-counts.

    // Commit Batch
    await batch.commit();

    // Separate User Stats Update
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    let newStreak = 1;
    const userData = userSnap.exists() ? userSnap.data() : {};

    if (userSnap.exists()) {
        newStreak = userData.stats?.winStreak || 0;
        newStreak = (newStreak || 0) + 1; 
    }

    const xpGain = 25;
    await setDoc(userRef, {
        stats: { xp: increment(xpGain), postsCount: increment(1), winStreak: newStreak },
        lastPostedAt: serverTimestamp()
    }, { merge: true });

    // --- BUFFERED XP UPDATE FOR HOUSE (Hot Doc Fix) ---
    if (userData.houseId) {
        try {
            await addDoc(collection(db, 'houseEvents'), {
                houseId: userData.houseId,
                xp: xpGain,
                createdAt: serverTimestamp()
            });
        } catch(e) { console.error("Could not buffer house XP", e); }
    }

    return { id: postRef.id, ...postData, createdAt: { seconds: Date.now() / 1000 } };
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
};

export const createBotPost = async (debateId, topicId, side, body, parentPostId=null) => {
    const postData = {
      debateId, topicId, uid: 'AI_BOT', side, type: 'rebuttal', body, parentPostId,
      createdAt: serverTimestamp(), upvotes: 0, downvotes: 0, aiAnalysis: null, flagCount: 0, insightCount: 0, isBot: true
    };
    return await addDoc(collection(db, 'posts'), postData);
};

export const updatePost = async (postId, newBody, newSourceUrl) => {
  if (!auth.currentUser) throw new Error("Must be logged in");
  try {
    await updateDoc(doc(db, 'posts', postId), {
      body: newBody, sourceUrl: newSourceUrl, editedAt: serverTimestamp(), aiAnalysis: null 
    });
    return true;
  } catch (error) { throw error; }
};

// --- CONTENT REPORTING (Apple Compliance) ---
// Server-authoritative reporting: unique per account, and when a post crosses the distinct-flag
// threshold the backend auto-hides it, cancels its points, opens an AI-reviewed moderation case,
// and (if unreviewed) auto-applies the AI's decision. Flag counts can't be spoofed client-side.
export const flagPost = async (postId, reason = "inappropriate") => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    return await callFirebaseFunction('flagPostSecurely', { postId, reason });
};

// Admin moderation page.
export const getModerationCases = async (status = 'pending_review') => {
    try {
        const res = await callFirebaseFunction('getModerationCases', { status });
        return res?.cases || [];
    } catch (error) {
        console.error("Error fetching moderation cases:", error);
        throw error;
    }
};
export const resolveModerationCase = async (caseId, decision) => {
    return await callFirebaseFunction('resolveModerationCase', { caseId, decision });
};

export const flagUser = async (targetUid, reason = "abusive_behavior") => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;

    const reportRef = doc(collection(db, 'reportedContent'));
    await setDoc(reportRef, {
        reportedBy: uid,
        contentType: 'user',
        contentId: targetUid,
        reason,
        status: 'pending',
        createdAt: serverTimestamp(),
        resolvedAt: null,
        resolvedBy: null,
        actionTaken: null
    });
};

export const deletePost = async (postId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'posts', postId));
};

// --- VOTE & REACTION & AWARD FUNCTIONS ---

export const giveInsightAward = async (postId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const senderUid = auth.currentUser.uid;
    const postRef = doc(db, 'posts', postId);
    const awardRef = doc(db, 'posts', postId, 'awards', senderUid);
    const userRef = doc(db, 'users', senderUid); 

    try {
        await runTransaction(db, async (transaction) => {
            const postSnap = await transaction.get(postRef);
            if (!postSnap.exists()) throw new Error("Post not found");
            const post = postSnap.data();
            
            if (post.uid === senderUid) throw new Error("Cannot award your own post.");

            const awardSnap = await transaction.get(awardRef);
            if (awardSnap.exists()) throw new Error("You have already awarded this post.");

            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("User profile not found.");
            const userData = userSnap.data();
            const inventoryCount = userData.inventory?.insight_bomb || 0;

            if (inventoryCount < 1) {
                throw new Error("Insufficient Insight Bombs. Purchase more in the Store.");
            }

            // CROSS-PARTISAN XP LOGIC
            let xpReward = XP_REWARDS.NEUTRAL;
            if (post.debateId) {
                const stanceRef = doc(db, 'debates', post.debateId, 'stances', senderUid);
                const stanceSnap = await transaction.get(stanceRef);
                const userSide = stanceSnap.exists() ? stanceSnap.data().side : null;
                
                if (userSide && post.side) {
                    if (userSide !== post.side) xpReward = XP_REWARDS.OPPOSITE_SIDE; 
                    else xpReward = XP_REWARDS.SAME_SIDE;
                }
            }

            // Update Sender's Inventory & XP
            transaction.update(userRef, {
                'inventory.insight_bomb': increment(-1),
                'stats.xp': increment(xpReward) 
            });

            // Buffer Sender's House XP
            if (userData.houseId) {
                const senderEventRef = doc(collection(db, 'houseEvents'));
                transaction.set(senderEventRef, {
                    houseId: userData.houseId,
                    xp: xpReward,
                    createdAt: serverTimestamp()
                });
            }

            transaction.set(awardRef, { uid: senderUid, awardedAt: serverTimestamp() });
            transaction.update(postRef, { insightCount: increment(1) });

            // Buffer Author's House Rep/Insight
            if (post.uid !== 'AI_BOT') {
                const authorRef = doc(db, 'users', post.uid);
                const authorSnap = await transaction.get(authorRef);
                
                if (authorSnap.exists()) {
                    transaction.set(authorRef, { stats: { repTotal: increment(REP_VALUES.INSIGHT_AWARD) } }, { merge: true });
                    
                    if (authorSnap.data().houseId) {
                        const authorEventRef = doc(collection(db, 'houseEvents'));
                        transaction.set(authorEventRef, {
                            houseId: authorSnap.data().houseId,
                            rep: REP_VALUES.INSIGHT_AWARD,
                            insight: 1,
                            createdAt: serverTimestamp()
                        });
                    }
                }
            }
            
            if (post.topicId) {
                const topicRef = doc(db, 'topics', post.topicId);
                transaction.set(topicRef, { insightCount: increment(1) }, { merge: true });
            }
        });
        return true;
    } catch (error) { throw error; }
};

export const hasUserAwardedInsight = async (postId, uid) => {
    if (!uid) return false;
    try {
        const snap = await getDoc(doc(db, 'posts', postId, 'awards', uid));
        return snap.exists();
    } catch (e) { return false; }
};

// --- FIX: ROBUST VOTE LOGIC ---
export const savePostVote = async (postId, voterUid, newVoteType) => {
  if (!voterUid) throw new Error("User must be logged in.");
  
  try {
    await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const voteRef = doc(db, 'posts', postId, 'votes', voterUid);
        
        // 1. READ ALL DATA FIRST
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error("Post does not exist!");
        const post = postSnap.data();
        
        // --- STRICT SELF-VOTE BLOCK ---
        if (post.uid === voterUid) {
            throw new Error("Self-voting is disabled.");
        }
        
        const voteSnap = await transaction.get(voteRef);
        const oldVoteType = voteSnap.exists() ? voteSnap.data().type : null;

        if (oldVoteType === newVoteType) return; // No change needed

        let repChange = 0;
        // Determine Rep Change
        if (oldVoteType === null) {
             if (newVoteType === 'up') repChange = REP_VALUES.UPVOTE;
             if (newVoteType === 'down') repChange = REP_VALUES.DOWNVOTE;
        }
        else if (oldVoteType === 'up') {
            if (newVoteType === null) repChange = -REP_VALUES.UPVOTE;
            if (newVoteType === 'down') repChange = -REP_VALUES.UPVOTE + REP_VALUES.DOWNVOTE;
        }
        else if (oldVoteType === 'down') {
            if (newVoteType === null) repChange = -REP_VALUES.DOWNVOTE;
            if (newVoteType === 'up') repChange = -REP_VALUES.DOWNVOTE + REP_VALUES.UPVOTE;
        }

        // Determine if we need to update Author
        let authorSnap = null;
        let authorRef = null;
        
        // Check if we need to update the author (not self, not bot)
        if (repChange !== 0 && post.uid !== 'AI_BOT') {
            authorRef = doc(db, 'users', post.uid);
            authorSnap = await transaction.get(authorRef);
        }

        // 2. WRITE OPERATIONS
        
        // Update Vote Doc
        if (newVoteType === null) { transaction.delete(voteRef); } 
        else { transaction.set(voteRef, { type: newVoteType, votedAt: serverTimestamp() }); }

        // Update Post Counts - Explicit Logic
        let upInc = 0;
        let downInc = 0;

        // REMOVED: Subtracting downvotes (logic fix)
        // If old was up, remove it.
        if (oldVoteType === 'up') {
            upInc = -1;
        } 
        // If old was down, we do NOTHING to the counts because we never added it in the first place.
        // (Unless we had old data where downvotes were counted, but for new logic: downvotes don't count).

        // If new is up, add it.
        if (newVoteType === 'up') {
            upInc += 1;
        }
        // If new is down, we do NOTHING to the counts.
        // Downvotes are purely for user state (red button) and rep calculation, but don't affect score.

        transaction.update(postRef, { 
            upvotes: increment(upInc), 
            // downvotes: increment(downInc) // DISABLED: Downvotes no longer tracked on post doc
        });

        // Update Author (if applicable)
        if (authorSnap && authorSnap.exists()) {
            transaction.set(authorRef, { stats: { repTotal: increment(repChange) } }, { merge: true });

            if (authorSnap.data().houseId) {
                // BUFFERED UPDATE for House Rep
                const eventRef = doc(collection(db, 'houseEvents'));
                transaction.set(eventRef, {
                    houseId: authorSnap.data().houseId,
                    rep: repChange,
                    createdAt: serverTimestamp()
                });
            }
        }
    });
    return true;
  } catch (error) { throw error; }
};

export const getSinglePostVote = async (postId, userId) => {
   if (!userId) return null;
   try {
     const voteSnap = await getDoc(doc(db, 'posts', postId, 'votes', userId));
     return voteSnap.exists() ? voteSnap.data().type : null;
   } catch (e) { return null; }
};

export const toggleReaction = async (postId, reactionType) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;
    const reactionRef = doc(db, 'posts', postId, 'reactions', uid);
    const postRef = doc(db, 'posts', postId);
    const userRef = doc(db, 'users', uid);

    const INVENTORY_MAP = {
        'gavel': 'logic_bomb',
        'book': 'evidence_bomb',
        'smart': 'insight_bomb'
    };
    const inventoryKey = INVENTORY_MAP[reactionType];

    await runTransaction(db, async (transaction) => {
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error("Post not found");
        const post = postSnap.data();
        const topicId = post.topicId;
        const debateId = post.debateId;
        
        let topicRef = null;
        if (topicId) topicRef = doc(db, 'topics', topicId);

        let userSide = null;
        if (debateId) {
            const stanceRef = doc(db, 'debates', debateId, 'stances', uid);
            const stanceSnap = await transaction.get(stanceRef);
            if (stanceSnap.exists()) {
                userSide = stanceSnap.data().side;
            }
        }

        const reactionSnap = await transaction.get(reactionRef);
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) throw new Error("User not found");
        const userData = userSnap.data();

        let xpReward = XP_REWARDS.NEUTRAL;
        if (userSide && post.side) {
            if (userSide !== post.side) xpReward = XP_REWARDS.OPPOSITE_SIDE; 
            else xpReward = XP_REWARDS.SAME_SIDE;
        }

        // --- INVENTORY LOGIC ---
        if (inventoryKey) {
            if (!reactionSnap.exists()) {
                // New Reaction - Deduct Item
                const currentCount = userData.inventory?.[inventoryKey] || 0;
                if (currentCount < 1) throw new Error(`Insufficient ${reactionType}s.`);
                
                transaction.update(userRef, { 
                    [`inventory.${inventoryKey}`]: increment(-1),
                    'stats.xp': increment(xpReward) 
                });
                
                // Buffer House XP
                if (userData.houseId) {
                    const eventRef = doc(collection(db, 'houseEvents'));
                    transaction.set(eventRef, {
                        houseId: userData.houseId,
                        xp: xpReward,
                        createdAt: serverTimestamp()
                    });
                }
            } else if (reactionSnap.data().type === reactionType) {
                // Same reaction - Do nothing (prevent un-toggle of paid items if we want strict consumption, or allow toggle but no refund)
                // For simplicity: Gift transactions are final (as per logic below)
                throw new Error("Gift transaction is final."); 
            } else {
                // Switching reaction type - Charge again
                const currentCount = userData.inventory?.[inventoryKey] || 0;
                if (currentCount < 1) throw new Error(`Insufficient ${reactionType}s.`);
                
                transaction.update(userRef, { 
                    [`inventory.${inventoryKey}`]: increment(-1),
                    'stats.xp': increment(xpReward)
                });
                
                // Buffer House XP
                if (userData.houseId) {
                    const eventRef = doc(collection(db, 'houseEvents'));
                    transaction.set(eventRef, {
                        houseId: userData.houseId,
                        xp: xpReward,
                        createdAt: serverTimestamp()
                    });
                }
            }
        }

        if (reactionSnap.exists()) {
            const existingType = reactionSnap.data().type;
            transaction.update(postRef, { 
                [`reactions.${existingType}`]: increment(-1),
                [`reactions.${reactionType}`]: increment(1) 
            });

            if (topicRef) {
                transaction.set(topicRef, { 
                    reactionCounts: { 
                        [existingType]: increment(-1),
                        [reactionType]: increment(1) 
                    } 
                }, { merge: true });
            }
            transaction.update(reactionRef, { type: reactionType, createdAt: serverTimestamp() });

        } else {
            transaction.set(reactionRef, { type: reactionType, createdAt: serverTimestamp() });
            transaction.update(postRef, { [`reactions.${reactionType}`]: increment(1) });
            
            if (topicRef) {
                transaction.set(topicRef, { reactionCounts: { [reactionType]: increment(1) } }, { merge: true });
            }
        }
    });
};

export const getUserReaction = async (postId, uid) => {
    if (!uid) return null;
    try {
        const snap = await getDoc(doc(db, 'posts', postId, 'reactions', uid));
        return snap.exists() ? snap.data().type : null;
    } catch (e) { return null; }
};

// --- ARENA FUNCTIONS ---
export const getOpenLobbies = async () => {
    try {
        const q = query(
            collection(db, 'debates'), 
            where('type', '==', 'turn_based'), 
            where('status', '==', 'recruiting'), 
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(game => game.visibility === 'public');
    } catch (error) { 
        console.error("Error fetching open lobbies:", error);
        return []; 
    }
};

export const createArenaGame = async (topicId, teamSize = 3, mode = 'classic', visibility = 'public', options = {}) => {
    try {
        const result = await callFirebaseFunction('createArenaSecurely', {
            topicId, 
            teamSize, 
            mode, 
            visibility,
            useHumanModerator: options.useHumanModerator,
            structureMode: options.structureMode 
        });
        return result.id;
    } catch (error) {
        console.error("Error creating arena game:", error);
        throw error;
    }
};

export const createCustomArenaGame = async (topicData, teamSize, mode, visibility, options = {}) => {
    try {
        const result = await callFirebaseFunction('createArenaSecurely', {
            topicId: null, 
            teamSize, 
            mode, 
            visibility,
            customTopicData: topicData,
            useHumanModerator: options.useHumanModerator,
            structureMode: options.structureMode 
        });
        return result.id;
    } catch (error) {
        console.error("Error creating custom arena game:", error);
        throw error;
    }
};

export const enterArenaLobby = async (topicId, teamSize = 3) => {
    const q = query(collection(db, 'debates'), where('type', '==', 'turn_based'), where('topicId', '==', topicId), where('status', '==', 'recruiting'), where('visibility', '==', 'public'), orderBy('createdAt', 'asc'), limit(1));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) return snapshot.docs[0].id;
    return createArenaGame(topicId, teamSize, 'classic', 'public'); 
};

export const joinPrivateGame = async (code) => {
    if (!code) throw new Error("Code required");
    const q = query(collection(db, 'debates'), where('joinCode', '==', code.toUpperCase().trim()), where('status', '==', 'recruiting'), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) throw new Error("Invalid code or lobby is no longer recruiting.");
    return snapshot.docs[0].id;
};

export const getMyPrivateLobbies = async (uid) => {
    if (!uid) return [];
    try {
        const q = query(
            collection(db, 'debates'),
            where('ownerId', '==', uid),
            where('status', '==', 'recruiting')
        );
        const snapshot = await getDocs(q);
        const lobbies = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(l => l.visibility === 'private' && l.type === 'turn_based')
            .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
            
        return lobbies;
    } catch (error) {
        console.error("Error fetching my private lobbies:", error);
        return [];
    }
};

export const getMyActiveMatches = async (uid) => {
    if (!uid) return [];
    try {
        // Regular arena matches
        const ownerQ = query(
            collection(db, 'debates'),
            where('ownerId', '==', uid),
            where('status', 'in', ['recruiting', 'active'])
        );

        const teamAQ = query(
            collection(db, 'debates'),
            where('teams.A.members', 'array-contains', uid),
            where('status', 'in', ['recruiting', 'active'])
        );

        const teamBQ = query(
            collection(db, 'debates'),
            where('teams.B.members', 'array-contains', uid),
            where('status', 'in', ['recruiting', 'active'])
        );

        // 10v1 games - as defender
        const defenderQ = query(
            collection(db, 'debates'),
            where('type', '==', '10v1'),
            where('defender', '==', uid),
            where('status', 'in', ['lobby', 'active'])
        );

        // 10v1 games - as challenger
        const challengerQ = query(
            collection(db, 'debates'),
            where('type', '==', '10v1'),
            where('challengers', 'array-contains', uid),
            where('status', 'in', ['lobby', 'active'])
        );

        const [ownerSnap, teamASnap, teamBSnap, defenderSnap, challengerSnap] = await Promise.all([
            getDocs(ownerQ),
            getDocs(teamAQ),
            getDocs(teamBQ),
            getDocs(defenderQ),
            getDocs(challengerQ)
        ]);

        const matches = {};

        const processDoc = (doc) => {
            if (!matches[doc.id]) {
                matches[doc.id] = { id: doc.id, ...doc.data() };
            }
        };

        ownerSnap.forEach(processDoc);
        teamASnap.forEach(processDoc);
        teamBSnap.forEach(processDoc);
        defenderSnap.forEach(processDoc);
        challengerSnap.forEach(processDoc);

        return Object.values(matches).sort((a, b) => {
            const dateA = a.lastPostedAt?.seconds || a.createdAt?.seconds || 0;
            const dateB = b.lastPostedAt?.seconds || b.createdAt?.seconds || 0;
            return dateB - dateA;
        });

    } catch (error) {
        console.error("Error fetching active matches:", error);
        toast.error("Failed to load your matches");
        return [];
    }
};

export const joinArenaTeamSecurely = async (debateId, side, joinCode = null) => {
    try {
        await callFirebaseFunction('joinArena', { debateId, side, joinCode });
        return true;
    } catch (error) {
        console.error("Error joining arena team:", error);
        throw error;
    }
};

export const startArenaGame = async (debateId) => {
    try {
        await callFirebaseFunction('startArenaGame', { debateId });
        return true;
    } catch (error) {
        console.error("Error starting arena:", error);
        throw error;
    }
};

export const completeTurn = async (debateId, currentSide) => {
    const gameRef = doc(db, 'debates', debateId);
    const gameSnap = await getDoc(gameRef);
    if (!gameSnap.exists()) return;
    const game = gameSnap.data();
    if (game.structure && game.currentTurnIndex !== undefined) {
        const nextIndex = game.currentTurnIndex + 1;
        if (nextIndex < game.structure.length) {
            const nextTurnDef = game.structure[nextIndex];
            await updateDoc(gameRef, { 
                currentTurnIndex: nextIndex, 
                currentTurn: nextTurnDef.turn, 
                activeSide: nextTurnDef.side, 
                turnPhaseName: nextTurnDef.phaseName, 
                turnPhase: 'drafting', 
                turnStartedAt: serverTimestamp() 
            });
        } else {
            await updateDoc(gameRef, { status: 'completed', turnPhase: 'judging', turnStartedAt: null });
        }
    } else {
        await updateDoc(gameRef, { 
            activeSide: currentSide === 'A' ? 'B' : 'A',
            turnStartedAt: serverTimestamp() 
        });
    }
};

export const voteForArenaTopic = async (gameId, topicId, userId) => {
    const gameRef = doc(db, 'debates', gameId);
    await updateDoc(gameRef, { [`topicVotes.${userId}`]: topicId });
};

export const submitDraftCard = async (debateId, turnIndex, side, body, uid) => {
    if (!debateId || turnIndex === undefined || !side) return;
    const draftsRef = collection(db, 'debates', debateId, 'turns', `${turnIndex}`, 'drafts');
    await addDoc(draftsRef, { uid, side, body, votes: 0, createdAt: serverTimestamp() });
};

export const voteForDraftCard = async (debateId, turnIndex, draftId) => {
    const draftRef = doc(db, 'debates', debateId, 'turns', `${turnIndex}`, 'drafts', draftId);
    await updateDoc(draftRef, { votes: increment(1) });
};

export const resolveTurn = async (debateId, currentTurnIndex, structure) => {
    const gameRef = doc(db, 'debates', debateId);
    const currentTurnDef = structure[currentTurnIndex];
    const draftsRef = collection(db, 'debates', debateId, 'turns', `${currentTurnIndex}`, 'drafts');
    const snapshot = await getDocs(draftsRef);
    const drafts = snapshot.docs.map(d => d.data());
    const activeSide = currentTurnDef.side;
    const winningDraft = drafts.filter(d => d.side === activeSide).sort((a, b) => b.votes - a.votes)[0];
    if (winningDraft) {
        await createPost({ debateId, topicId: 'N/A', side: activeSide, type: currentTurnDef.type, body: winningDraft.body, turnNumber: currentTurnDef.turn, phaseName: currentTurnDef.phaseName, debateType: 'turn_based' });
    }
    await completeTurn(debateId, activeSide);
};

// --- USER FUNCTIONS ---
export const getUserProfile = async (userId) => {
  try {
    if (userId === 'AI_BOT') { return { handle: 'Verbi AI', photoURL: 'https://api.dicebear.com/9.x/bottts/svg?seed=VerbiAI', isBot: true }; }
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() };
    return null;
  } catch (error) { return null; }
};

export const getUserStats = async (userId) => {
   const profile = await getUserProfile(userId);
   return profile ? profile.stats : null;
};

// --- FOLLOW LOGIC ---
export const followUser = async (targetUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const currentUid = auth.currentUser.uid;
    if (currentUid === targetUid) throw new Error("Cannot follow yourself");

    const batch = writeBatch(db);
    
    const followingRef = doc(db, 'users', currentUid, 'following', targetUid);
    batch.set(followingRef, { uid: targetUid, followedAt: serverTimestamp() });

    const followerRef = doc(db, 'users', targetUid, 'followers', currentUid);
    batch.set(followerRef, { uid: currentUid, followedAt: serverTimestamp() });

    // Update counts
    batch.update(doc(db, 'users', currentUid), { followingCount: increment(1) });
    batch.update(doc(db, 'users', targetUid), { followersCount: increment(1) });

    await batch.commit();
};

export const unfollowUser = async (targetUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const currentUid = auth.currentUser.uid;

    const batch = writeBatch(db);
    
    batch.delete(doc(db, 'users', currentUid, 'following', targetUid));
    batch.delete(doc(db, 'users', targetUid, 'followers', currentUid));

    batch.update(doc(db, 'users', currentUid), { followingCount: increment(-1) });
    batch.update(doc(db, 'users', targetUid), { followersCount: increment(-1) });

    await batch.commit();
};

export const getIsFollowing = async (currentUid, targetUid) => {
    if (!currentUid || !targetUid) return false;
    const snap = await getDoc(doc(db, 'users', currentUid, 'following', targetUid));
    return snap.exists();
};

export const updateUserProfile = async (uid, data) => {
    if (!auth.currentUser || auth.currentUser.uid !== uid) throw new Error("Permission denied");
    await updateDoc(doc(db, 'users', uid), data);
};

export const getUserFollowing = async (uid) => {
    try {
        const q = query(collection(db, 'users', uid, 'following'), orderBy('followedAt', 'desc'));
        const snap = await getDocs(q);
        const uids = snap.docs.map(d => d.id);
        
        const results = await Promise.allSettled(uids.map(async (id) => {
            const profile = await getUserProfile(id);
            if (profile) profile.isFollowing = true;
            return profile;
        }));
        return results
            .filter(r => r.status === 'fulfilled' && r.value !== null)
            .map(r => r.value);
    } catch (e) { return []; }
};

export const getUserFollowers = async (uid) => {
    try {
        const q = query(collection(db, 'users', uid, 'followers'), orderBy('followedAt', 'desc'));
        const snap = await getDocs(q);
        const uids = snap.docs.map(d => d.id);
        
        const currentUid = auth.currentUser?.uid;
        
        const results = await Promise.allSettled(uids.map(async (id) => {
            const profile = await getUserProfile(id);
            if (profile && currentUid) {
                const isFollowing = await getIsFollowing(currentUid, id);
                profile.isFollowing = isFollowing;
            }
            return profile;
        }));
        return results
            .filter(r => r.status === 'fulfilled' && r.value !== null)
            .map(r => r.value);
    } catch (e) { return []; }
};


// --- AI & ANALYTICS ---

export const getGlobalStagingTopics = async () => {
    try {
        const q = query(collection(db, 'globalTopics'), where('status', '==', 'pending_approval'), orderBy('isoDate', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching global staging topics:", error);
        return [];
    }
};

export const approveGlobalTopic = async (topic) => {
    if (!auth.currentUser) throw new Error("Must be logged in");

    const newTopic = {
        title: topic.title,
        summary: topic.summary,
        category: topic.category,
        region: topic.region,
        sourceLink: topic.link || null,
        image: topic.image || null, // Include RSS/OG image
        audioUrl: topic.audioUrl || null, // Include podcast audio if available
        audioType: topic.audioType || null,
        status: 'active',
        communityId: null,
        createdBy: 'admin_global',
        createdAt: serverTimestamp(),
        postCount: 0,
        commentCount: 0,
        insightCount: 0,
        flagCount: 0,
        sides: [{ key: 'A', label: 'For' }, { key: 'B', label: 'Against' }],
        isSpotlight: true
    };
    await addDoc(collection(db, 'topics'), newTopic);

    await deleteDoc(doc(db, 'globalTopics', topic.id));
};

export const rejectGlobalTopic = async (topicId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await deleteDoc(doc(db, 'globalTopics', topicId));
};

export const runNationalFeedUpdate = async ({ targetCountry, limit } = {}) => {
    try {
        const result = await callFirebaseFunction('runNationalFeedUpdate', { targetCountry, limit });
        return result;
    } catch (error) {
        console.error("Error running national feed update:", error);
        throw error;
    }
};

// Admin: clear auto-generated news topics with no debate activity (clean slate for the feed).
export const purgeNewsFeed = async () => {
    try {
        return await callFirebaseFunction('purgeNewsFeed', {});
    } catch (error) {
        console.error("Error purging news feed:", error);
        throw error;
    }
};

export const generateEvergreenTopics = async ({ count = 12 } = {}) => {
    try {
        const result = await callFirebaseFunction('generateEvergreenTopics', { count });
        return result;
    } catch (error) {
        console.error("Error generating evergreen topics:", error);
        throw error;
    }
};

// Admin: tally + award the outgoing Topic of the Day and pick a new one (also runs daily 09:00).
export const forceTopicOfDayCycle = async () => {
    try {
        const result = await callFirebaseFunction('forceTopicOfDayCycle', {});
        return result;
    } catch (error) {
        console.error("Error running topic-of-day cycle:", error);
        throw error;
    }
};

// Admin: push a "we've updated the app" notification to every user's device. Run after a release.
export const broadcastUpdateNotification = async ({ title, body, link } = {}) => {
    try {
        const result = await callFirebaseFunction('broadcastUpdateNotification', { title, body, link });
        return result;
    } catch (error) {
        console.error("Error broadcasting update notification:", error);
        throw error;
    }
};

// Reader: fetch + extract the real article body so users read the story in-app.
// Returns { ok, blocks: [{type:'h'|'p', text}], byline }. ok:false → show summary fallback.
export const getArticleContent = async ({ url, topicId } = {}) => {
    try {
        const result = await callFirebaseFunction('getArticleContent', { url, topicId });
        return result || { ok: false };
    } catch (error) {
        console.error("Error fetching article content:", error);
        return { ok: false, reason: 'error' };
    }
};

// Admin: opinion-insights data asset (aggregated, de-identified sentiment by category & region).
export const getOpinionInsights = async () => {
    try {
        return await callFirebaseFunction('getOpinionInsights', {});
    } catch (error) {
        console.error("Error fetching opinion insights:", error);
        throw error;
    }
};
export const forceAggregateInsights = async () => {
    try {
        return await callFirebaseFunction('forceAggregateInsights', {});
    } catch (error) {
        console.error("Error aggregating insights:", error);
        throw error;
    }
};

// Admin: manage external data-API keys (companies connect to the anonymized insights API).
export const createDataApiKey = async (label) => callFirebaseFunction('createDataApiKey', { label });
export const listDataApiKeys = async () => {
    const res = await callFirebaseFunction('listDataApiKeys', {});
    return res?.keys || [];
};
export const revokeDataApiKey = async (key) => callFirebaseFunction('revokeDataApiKey', { key });

// State news — pass a stateCode (preferred) or a ZIP we resolve to a state server-side.
// Shaped like the other feed fetchers ({ topics, hasMore }) so the feed hook can use it directly.
export const getLocalNews = async ({ stateCode, zip } = {}) => {
    try {
        const res = await callFirebaseFunction('getLocalNews', { stateCode, zip });
        return { topics: res?.topics || [], lastVisible: null, hasMore: false, stateCode: res?.stateCode || null, locality: res?.locality || null };
    } catch (error) {
        console.error("Error fetching state news:", error);
        return { topics: [], lastVisible: null, hasMore: false, error: error.message };
    }
};

// Admin: ingestion diagnostics — what the last news pull fetched and what the AI decided.
export const getIngestionStatus = async () => {
    try {
        return await callFirebaseFunction('getIngestionStatus', {});
    } catch (error) {
        console.error("Error fetching ingestion status:", error);
        throw error;
    }
};

export const runGlobalTopicsUpdate = async ({ targetCountry } = {}) => {
    try {
        const result = await callFirebaseFunction('runGlobalTopicsUpdate', { targetCountry });
        return result;
    } catch (error) {
        console.error("Error running global topics update:", error);
        throw error;
    }
};

export const runPoliticalCompassAnalysis = async (posts, topicMap) => {
    if (!posts || posts.length === 0) return null;
    try {
        const result = await callFirebaseFunction('getPoliticalCompass', { posts, topicMap });
        return result;
    } catch (error) {
        console.error("Error running political compass analysis:", error);
        return null;
    }
};

export const getCoachingDrill = async (uid) => {
    if (!uid) throw new Error("User required");
    
    try {
        const drill = await aiService.getAiSenseiDrill();
        return drill;
    } catch (error) {
        console.error("Error getting drill:", error);
        return { type: 'error', text: 'Could not load drill. Please try again.' };
    }
};

export const declareWinner = async (debateHistory, topic) => {
    return aiService.declareWinner(debateHistory, topic);
};

export const saveAiScore = async (postId, scoreData) => {
   try {
      const updates = { aiAnalysis: scoreData };
      if (scoreData.civility?.score !== undefined && scoreData.civility.score < 0.4) {
          updates.flagCount = increment(1);
      }
      await updateDoc(doc(db, 'posts', postId), updates);
      const avgScore = ((scoreData.logic?.score || 0) + (scoreData.evidence?.score || 0) + (scoreData.clarity?.score || 0)) / 3;
      if (avgScore >= 0.8) {
          const postSnap = await getDoc(doc(db, 'posts', postId));
          if (postSnap.exists()) {
              const post = postSnap.data();
              if (post.uid !== 'AI_BOT' && !post.hasAiBonus) {
                  await updateUserRep(post.uid, REP_VALUES.AI_BONUS);
                  await updateDoc(doc(db, 'posts', postId), { hasAiBonus: true });
              }
          }
      }
   } catch (e) { console.error("Could not save AI score/bonus:", e); }
};

export const getAiScoresForDebate = async (debateId) => {
    const posts = await getPostsForDebate(debateId);
    const scores = {};
    const postList = posts.posts || posts; 
    postList.forEach(p => { if (p.aiAnalysis) scores[p.id] = p.aiAnalysis; });
    return scores;
};

export const getUserAnalyticsData = async (uid) => {
    try {
        const postsQ = query(collection(db, 'posts'), where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(50));
        const postsSnap = await getDocs(postsQ);
        const posts = postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const topicIds = [...new Set(posts.map(p => p.topicId))];
        const topicMap = {};
        await Promise.all(topicIds.map(async (tid) => {
            if (!tid) return;
            try { const tDoc = await getDoc(doc(db, 'topics', tid)); if (tDoc.exists()) topicMap[tid] = tDoc.data().category; } catch (e) { }
        }));
        return { posts, topicMap };
    } catch (error) { return { posts: [], topicMap: {} }; }
};

// --- TOURNAMENT FUNCTIONS ---
export const getTournamentData = async () => {
    try {
        const docRef = doc(db, 'tournaments', 'current');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error("Error getting tournament data:", error);
        return null;
    }
};

export const getMyTournamentApplication = async (houseId) => {
    if (!houseId) return null;
    try {
        const docRef = doc(db, 'tournamentApplications', houseId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
        console.error("Error getting tournament application:", error);
        return null;
    }
};

export const applyForTournament = async (houseId) => {
    if (!auth.currentUser) throw new Error("Must be logged in.");
    
    const role = await getMyHouseRole(houseId, auth.currentUser.uid);
    if (role !== 'leader' && role !== 'moderator') {
        throw new Error("Only house leaders or moderators can apply for the tournament.");
    }
    
    const appRef = doc(db, 'tournamentApplications', houseId);
    const appSnap = await getDoc(appRef);
    if (appSnap.exists()) {
        throw new Error("Your house has already applied for the next tournament.");
    }

    const houseSnap = await getDoc(doc(db, 'houses', houseId));
    
    if (!houseSnap.exists()) {
        throw new Error("House not found.");
    }

    let seasonId = "Season 1"; 
    try {
        const settingsRef = doc(db, 'tournaments', 'settings');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists() && settingsSnap.data().system?.seasonName) {
            seasonId = settingsSnap.data().system.seasonName;
        }
    } catch (e) {
        console.warn("Could not fetch tournament settings, using default season.");
    }

    await setDoc(appRef, {
        houseId: houseId,
        houseName: houseSnap.data().name,
        appliedBy: auth.currentUser.uid,
        appliedAt: serverTimestamp(),
        status: "pending",
        season: seasonId 
    });

    return true;
};

export const getTournamentStandings = async () => {
    try {
        const result = await callFirebaseFunction('getTournamentStandings');
        return result.standings;
    } catch (error) {
        console.error("Error fetching tournament standings:", error);
        throw error;
    }
};

export const getHouseRosterAnalytics = async (houseId) => {
    try {
        const result = await callFirebaseFunction('getHouseRosterAnalytics', { houseId });
        return result;
    } catch (error) {
        console.error("Error fetching roster analytics:", error);
        throw error;
    }
};

export const substitutePlayer = async (houseId, slot) => {
    try {
        const result = await callFirebaseFunction('substituteTournamentPlayer', { houseId, slot });
        return result;
    } catch (error) {
        console.error("Error substituting player:", error);
        throw error;
    }
};

export const getTournamentSettings = async () => {
    try {
        const docRef = doc(db, 'tournaments', 'settings');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return {
            qualification: { engagementWeight: 1.0, repWeight: 0.1, xpWeight: 0.01, bracketSize: 8 },
            schedule: { autoStartDay: 'Saturday', autoStartTime: '09:00', roundDurationHours: 24 },
            matchRules: { topicCategory: 'Random', aiStrictness: 'high', turnTimeLimit: 86400 },
            rewards: { winnerRep: 500, winnerXp: 1000 }
        };
    } catch (error) {
        console.error("Error getting tournament settings:", error);
        return null;
    }
};

export const updateTournamentSettings = async (settings) => {
    if (!auth.currentUser) throw new Error("Must be logged in.");
    try {
        await setDoc(doc(db, 'tournaments', 'settings'), settings, { merge: true });
        return true;
    } catch (error) {
        console.error("Error updating settings:", error);
        throw error;
    }
};

// --- MONETIZATION FUNCTIONS ---

export const triggerReactionBomb = async (debateId, type, postId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;

    const TYPE_MAP = {
        'gavel': 'logic_bomb',
        'book': 'evidence_bomb',
        'smart': 'insight_bomb'
    };

    const inventoryKey = TYPE_MAP[type];
    if (!inventoryKey) throw new Error("Invalid bomb type");

    const userRef = doc(db, 'users', uid);

    try {
        await runTransaction(db, async (transaction) => {
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("User not found");

            const userData = userSnap.data();
            const currentCount = userData.inventory?.[inventoryKey] || 0;

            if (currentCount < 1) {
                throw new Error(`Out of ${type.replace('_', ' ')} bombs! Buy more in the Store.`);
            }

            // CROSS-PARTISAN XP LOGIC
            let xpReward = XP_REWARDS.NEUTRAL;
            if (debateId) {
                // Fetch the sender's stance
                const stanceRef = doc(db, 'debates', debateId, 'stances', uid);
                const stanceSnap = await transaction.get(stanceRef);
                const userSide = stanceSnap.exists() ? stanceSnap.data().side : null;
                
                // Fetch the post to see its side
                const postRef = doc(db, 'posts', postId);
                const postSnap = await transaction.get(postRef);
                const post = postSnap.exists() ? postSnap.data() : null;

                if (userSide && post && post.side) {
                    if (userSide !== post.side) xpReward = XP_REWARDS.OPPOSITE_SIDE; // +50 XP
                    else xpReward = XP_REWARDS.SAME_SIDE; // +10 XP
                }
            }

            // Update User Inventory & XP
            transaction.update(userRef, {
                [`inventory.${inventoryKey}`]: increment(-1),
                'stats.xp': increment(xpReward)
            });

            // Buffer House XP
            if (userData.houseId) {
                const eventRef = doc(collection(db, 'houseEvents'));
                transaction.set(eventRef, {
                    houseId: userData.houseId,
                    xp: xpReward,
                    createdAt: serverTimestamp()
                });
            }

            const bombRef = doc(collection(db, 'debates', debateId, 'reactionBombs'));
            transaction.set(bombRef, {
                debateId,
                postId,
                type,
                triggeredBy: uid,
                triggeredAt: serverTimestamp()
            });
        });

        return { success: true };

    } catch (error) {
        console.error("Reaction Bomb failed:", error);
        throw error;
    }
};

// --- NEW: REDEEM REFERRAL ---
export const redeemReferral = async (code) => {
    if (!auth.currentUser) throw new Error("Must be logged in.");
    try {
        const result = await callFirebaseFunction('redeemReferral', { code });
        return result;
    } catch (error) {
        console.error("Error redeeming referral:", error);
        throw error;
    }
};

// --- ADMIN FUNCTIONS ---

export const forceStartTournament = async () => {
    try {
        const result = await callFirebaseFunction('forceStartTournament');
        return result;
    } catch (error) {
        console.error("Error forcing tournament start:", error);
        throw error;
    }
};

export const getConquestState = async () => {
    try {
        const result = await callFirebaseFunction('getConquestState');
        return result;
    } catch (error) {
        console.error("Error fetching conquest state:", error);
        return { activeZones: [], owners: {} };
    }
};

export const claimHouseChest = async () => {
    try {
        const result = await callFirebaseFunction('claimHouseChest');
        return result;
    } catch (error) {
        console.error("Error claiming chest:", error);
        throw error;
    }
};

export const checkChestClaimStatus = async (houseId, uid, bountiesUpdatedAt) => {
    if (!houseId || !uid) return false;
    try {
        const ms = getTimestampMillis(bountiesUpdatedAt);
        const chestId = ms ? ms.toString() : 'initial';

        const docRef = doc(db, 'houses', houseId, 'claims', `${chestId}_${uid}`);
        const docSnap = await getDoc(docRef);
        return docSnap.exists();
    } catch (error) {
        console.error("Error checking claim status:", error);
        return false;
    }
};

export const forceBountyRotation = async () => {
    try {
        const result = await callFirebaseFunction('forceBountyRotation');
        return result;
    } catch (error) {
        console.error("Error forcing bounty rotation:", error);
        throw error;
    }
};

export const forceConquestUpdate = async () => {
    try {
        const result = await callFirebaseFunction('forceConquestUpdate');
        return result;
    } catch (error) {
        console.error("Error forcing conquest update:", error);
        throw error;
    }
};

// --- ACTIVITY METRICS FOR ADMIN DASHBOARD ---
export const getActivityMetrics = async () => {
    try {
        const docRef = doc(db, 'systemStats', 'activity');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;
    } catch (error) {
        console.error("Error fetching activity metrics:", error);
        return null;
    }
};

// ==============================================
// 10v1 ARENA FUNCTIONS
// ==============================================

/**
 * Create a new 10v1 game
 */
export const create10v1Game = async (topicId, customTopicData, visibility = 'public') => {
    try {
        return await callFirebaseFunction('create10v1Game', {
            topicId,
            customTopicData,
            visibility
        });
    } catch (error) {
        console.error("Error creating 10v1 game:", error);
        throw error;
    }
};

/**
 * Join a 10v1 game as a challenger
 */
export const join10v1Game = async (gameId, joinCode = null) => {
    try {
        return await callFirebaseFunction('join10v1Game', {
            gameId,
            joinCode
        });
    } catch (error) {
        console.error("Error joining 10v1 game:", error);
        throw error;
    }
};

/**
 * Submit an argument in a 10v1 game
 */
export const submit10v1Argument = async (gameId, body, role) => {
    try {
        return await callFirebaseFunction('submit10v1Argument', {
            gameId,
            body,
            role
        });
    } catch (error) {
        console.error("Error submitting argument:", error);
        throw error;
    }
};

/**
 * Submit a judge vote in a 10v1 game
 */
export const submit10v1Vote = async (gameId, vote) => {
    try {
        return await callFirebaseFunction('submit10v1Vote', {
            gameId,
            vote
        });
    } catch (error) {
        console.error("Error submitting vote:", error);
        throw error;
    }
};

/**
 * Advance 10v1 phase when timer expires
 * Called by frontend to trigger reliable phase transitions
 */
export const advance10v1Phase = async (gameId, expectedPhase) => {
    try {
        return await callFirebaseFunction('advance10v1Phase', {
            gameId,
            expectedPhase
        });
    } catch (error) {
        console.error("Error advancing phase:", error);
        throw error;
    }
};

/**
 * Start a 10v1 game immediately from lobby
 * Called when lobby timer expires to avoid scheduled function delay
 */
export const start10v1GameFromLobby = async (gameId) => {
    try {
        return await callFirebaseFunction('start10v1GameFromLobby', { gameId });
    } catch (error) {
        console.error("Error starting game from lobby:", error);
        throw error;
    }
};

/**
 * Skip a non-responsive challenger in 10v1
 * Called by defender when challenger times out or needs to be kicked
 */
export const skip10v1Challenger = async (gameId, reason = 'timeout') => {
    try {
        return await callFirebaseFunction('skip10v1Challenger', { gameId, reason });
    } catch (error) {
        console.error("Error skipping challenger:", error);
        throw error;
    }
};

/**
 * Flag an argument in 10v1 arena for moderation
 * Creates a report in reportedContent collection
 */
export const flag10v1Argument = async (gameId, argumentId, reason = 'inappropriate') => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Must be logged in to flag content");

    const reportRef = doc(collection(db, 'reportedContent'));
    await setDoc(reportRef, {
        reportedBy: uid,
        contentType: '10v1_argument',
        contentId: argumentId,
        gameId: gameId,
        reason: reason,
        status: 'pending',
        createdAt: serverTimestamp()
    });

    return { success: true, reportId: reportRef.id };
};

/**
 * Vote to kick a challenger from 10v1 arena
 * Requires majority of active challengers to agree
 */
export const voteKick10v1Challenger = async (gameId, targetUid, round) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Must be logged in to vote kick");

    // Store kick vote in a subcollection
    const voteRef = doc(db, 'debates', gameId, 'kickVotes', `${round}_${targetUid}_${uid}`);
    await setDoc(voteRef, {
        voter: uid,
        target: targetUid,
        round: round,
        createdAt: serverTimestamp()
    });

    // Check if majority reached - call backend to process
    return await callFirebaseFunction('processKickVote', { gameId, targetUid, round });
};

/**
 * Get completed/expired 10v1 games for history display
 * Includes both completed games and expired/abandoned games
 */
export const get10v1History = async (limitCount = 10) => {
    try {
        // Fetch completed games
        const completedQ = query(
            collection(db, 'debates'),
            where('type', '==', '10v1'),
            where('status', '==', 'completed'),
            orderBy('endedAt', 'desc'),
            limit(limitCount)
        );

        // Fetch expired/abandoned games
        const expiredQ = query(
            collection(db, 'debates'),
            where('type', '==', '10v1'),
            where('status', '==', 'expired'),
            orderBy('endedAt', 'desc'),
            limit(limitCount)
        );

        const [completedSnap, expiredSnap] = await Promise.all([
            getDocs(completedQ),
            getDocs(expiredQ)
        ]);

        const games = {};
        completedSnap.docs.forEach(d => {
            games[d.id] = { id: d.id, ...d.data() };
        });
        expiredSnap.docs.forEach(d => {
            games[d.id] = { id: d.id, ...d.data() };
        });

        // Sort by endedAt descending and limit
        return Object.values(games)
            .sort((a, b) => {
                const dateA = a.endedAt?.seconds || a.createdAt?.seconds || 0;
                const dateB = b.endedAt?.seconds || b.createdAt?.seconds || 0;
                return dateB - dateA;
            })
            .slice(0, limitCount);
    } catch (error) {
        console.error("Error fetching 10v1 history:", error);
        toast.error("Failed to load match history");
        return [];
    }
};

/**
 * Duplicate a completed 10v1 game as a new debate
 * Creates a new lobby with the same topic/title
 */
export const duplicate10v1Game = async (originalGameId) => {
    try {
        // Fetch the original game
        const gameDoc = await getDoc(doc(db, 'debates', originalGameId));
        if (!gameDoc.exists()) throw new Error("Original game not found");

        const originalGame = gameDoc.data();

        // Create new game with same topic data
        return await callFirebaseFunction('create10v1Game', {
            customTopicData: {
                title: originalGame.title,
                description: originalGame.description,
                openingStatement: originalGame.openingStatement
            },
            visibility: 'public' // New duplicates are always public
        });
    } catch (error) {
        console.error("Error duplicating 10v1 game:", error);
        throw error;
    }
};

// --- NOTIFICATION FUNCTIONS ---
export const getNotifications = async (uid) => {
    if (!uid) return [];
    try {
        const q = query(
            collection(db, 'notifications', uid, 'userNotifications'),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error fetching notifications:", error);
        toast.error("Couldn't load notifications");
        return [];
    }
};

export const markNotificationRead = async (uid, notificationId) => {
    if (!uid || !notificationId) return;
    try {
        const ref = doc(db, 'notifications', uid, 'userNotifications', notificationId);
        await updateDoc(ref, { read: true });
    } catch (error) {
        console.error("Error marking notification read:", error);
    }
};

export const markAllNotificationsRead = async (uid) => {
    if (!uid) return;
    try {
        const q = query(
            collection(db, 'notifications', uid, 'userNotifications'),
            where('read', '==', false)
        );
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
            batch.update(doc.ref, { read: true });
        });
        await batch.commit();
    } catch (error) {
        console.error("Error marking all read:", error);
    }
};

export const saveFcmToken = async (uid, token) => {
  if (!uid || !token) return;
  const userRef = doc(db, 'users', uid);
  try {
    await updateDoc(userRef, {
      fcmTokens: arrayUnion(token)
    });
  } catch (e) {
    console.error("Error saving FCM token:", e);
  }
};

export const getCommunityFlaggedReports = async (communityId) => {
    try {
        // 1. Flagged Topics
        const topicsQ = query(
            collection(db, 'topics'),
            where('communityId', '==', communityId),
            where('flagCount', '>', 0),
            orderBy('flagCount', 'desc')
        );
        const topicsSnap = await getDocs(topicsQ);
        const topics = topicsSnap.docs.map(d => ({ id: d.id, type: 'topic', ...d.data() }));

        // 2. Flagged Posts (Two-step Strategy)
        const recentTopicsQ = query(collection(db, 'topics'), where('communityId', '==', communityId), limit(20));
        const recentTopicsSnap = await getDocs(recentTopicsQ);
        const topicIds = recentTopicsSnap.docs.map(d => d.id);

        let posts = [];
        if (topicIds.length > 0) {
            const safeIds = topicIds.slice(0, 30); 
            const postsQ = query(
                collection(db, 'posts'),
                where('topicId', 'in', safeIds),
                where('flagCount', '>', 0),
                orderBy('flagCount', 'desc')
            );
            const postsSnap = await getDocs(postsQ);
            posts = postsSnap.docs.map(d => ({ id: d.id, type: 'post', ...d.data() }));
        }

        return [...topics, ...posts].sort((a,b) => b.flagCount - a.flagCount);
    } catch (e) {
        console.error("Error fetching flagged reports:", e);
        return [];
    }
};

export const clearContentFlags = async (itemId, type) => {
    if (!itemId) return;
    const collectionName = type === 'topic' ? 'topics' : 'posts';
    await updateDoc(doc(db, collectionName, itemId), { flagCount: 0 });
};

// --- NEW: DELETE NOTIFICATION ---
export const deleteNotification = async (uid, notificationId) => {
    if (!uid || !notificationId) return;
    try {
        await deleteDoc(doc(db, 'notifications', uid, 'userNotifications', notificationId));
    } catch (error) {
        console.error("Error deleting notification:", error);
    }
};

// --- NEW: DELETE ALL NOTIFICATIONS ---
export const deleteAllNotifications = async (uid) => {
    if (!uid) return;
    try {
        const q = collection(db, 'notifications', uid, 'userNotifications');
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
        });
        await batch.commit();
    } catch (error) {
        console.error("Error deleting all notifications:", error);
    }
};

// --- NEW: SUBMIT FEEDBACK ---
export const submitFeedback = async (rating, feedback) => {
    if (!auth.currentUser) throw new Error("Must be logged in.");
    try {
        await addDoc(collection(db, 'feedback'), {
            uid: auth.currentUser.uid,
            rating,
            feedback,
            createdAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error("Error submitting feedback:", error);
        throw error;
    }
};

// --- NEW: USER PREFERENCE FUNCTIONS ---
export const saveUserPreference = async (uid, key, value) => {
    if (!uid) return;
    try {
        const userRef = doc(db, 'users', uid);
        await updateDoc(userRef, {
            [key]: value
        });
    } catch (e) {
        console.error(`Error saving user preference ${key}:`, e);
        // Fallback for "No document to update" on rare race conditions
        if (e.code === 'not-found') {
             try {
                 await setDoc(doc(db, 'users', uid), { [key]: value }, { merge: true });
             } catch(err) { console.error("Critical pref save fail:", err); }
        }
    }
};

// --- NEW: ACCOUNT DELETION (REQUIRED FOR APPLE) ---
export const deleteUserAccount = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error("No user logged in.");
    
    try {
        // 1. Delete Firestore Profile (Client-side initiation)
        // Note: A Cloud Function trigger usually handles recursive deletion of subcollections
        await deleteDoc(doc(db, 'users', user.uid));
        
        // 2. Delete Auth Account
        // This will throw an error if the user hasn't logged in recently (requires re-auth),
        // but it satisfies the basic UI requirement.
        await user.delete();
        return true;
    } catch (error) {
        console.error("Account deletion failed:", error);
        throw error;
    }
};

// --- BLOCKING (Compliance) ---
export const blockUser = async (targetUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;
    if (uid === targetUid) throw new Error("Cannot block yourself");

    // 1. Add to blocked collection
    await setDoc(doc(db, 'users', uid, 'blocked', targetUid), {
        uid: targetUid,
        blockedAt: serverTimestamp()
    });

    // 2. Auto-unfollow if following
    try {
        await unfollowUser(targetUid);
    } catch (e) {
        // Ignore if not following
    }
};

export const unblockUser = async (targetUid) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const uid = auth.currentUser.uid;
    await deleteDoc(doc(db, 'users', uid, 'blocked', targetUid));
};

export const getBlockedUsers = async (uid) => {
    if (!uid) return [];
    try {
        const q = collection(db, 'users', uid, 'blocked');
        const snap = await getDocs(q);
        return snap.docs.map(doc => doc.id);
    } catch (e) {
        console.error("Error fetching blocked users:", e);
        return [];
    }
};

// --- CONTENT MODERATION (Apple Compliance) ---
export const getReportedContent = async (status = 'pending', limitCount = 50) => {
    try {
        let q = query(
            collection(db, 'reportedContent'),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
        );

        if (status !== 'all') {
            q = query(
                collection(db, 'reportedContent'),
                where('status', '==', status),
                orderBy('createdAt', 'desc'),
                limit(limitCount)
            );
        }

        const snap = await getDocs(q);
        return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
        console.error("Error fetching reported content:", e);
        return [];
    }
};

export const resolveReport = async (reportId, actionTaken, shouldBanUser = false) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const adminUid = auth.currentUser.uid;

    const reportRef = doc(db, 'reportedContent', reportId);
    const reportSnap = await getDoc(reportRef);

    if (!reportSnap.exists()) throw new Error("Report not found");

    const report = reportSnap.data();

    // Update report status
    await updateDoc(reportRef, {
        status: 'resolved',
        resolvedAt: serverTimestamp(),
        resolvedBy: adminUid,
        actionTaken
    });

    // If action is to remove content
    if (actionTaken === 'content_removed' && report.contentType === 'post') {
        try {
            await deleteDoc(doc(db, 'posts', report.contentId));
        } catch (e) {
            console.error("Error deleting post:", e);
        }
    }

    // If we need to ban the user who posted the content
    if (shouldBanUser && report.contentId) {
        try {
            // Get the offending content to find the author
            let authorUid = null;

            if (report.contentType === 'post') {
                const postSnap = await getDoc(doc(db, 'posts', report.contentId));
                if (postSnap.exists()) {
                    authorUid = postSnap.data().uid;
                }
            } else if (report.contentType === 'user') {
                authorUid = report.contentId;
            }

            if (authorUid) {
                await updateDoc(doc(db, 'users', authorUid), {
                    banned: true,
                    bannedAt: serverTimestamp(),
                    bannedBy: adminUid,
                    banReason: report.reason
                });
            }
        } catch (e) {
            console.error("Error banning user:", e);
        }
    }
};